import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiArrowLeft } from "react-icons/hi";
import Dashboard from "../../common/components/layout/Dashboard";
import { numberToCurrency } from "../../common/helpers/currency.helpers";
import {
  DebitManagementDashboardData,
  getDebitManagementDashboard,
} from "../../common/services/debit-management.api";
import { useCompanyStore } from "@/common/states/store/Company";

function ReceivablesDashboardPage() {
  const navigate = useNavigate();
  const companyStore = useCompanyStore();
  const [dashboard, setDashboard] = useState<DebitManagementDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getDebitManagementDashboard();

      if (result.ok) {
        setDashboard(result.data);
      } else {
        setDashboard(null);
      }

      setIsLoading(false);
    })();
  }, [companyStore]);

  return (
    <Dashboard>
      <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/financeiro/gestao-cobranca")}
              className="p-2 rounded-lg hover:bg-gray-100 transition cursor-pointer"
              title="Voltar"
            >
              <HiArrowLeft className="text-xl text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Dashboard - Gestão de Cobranças
              </h1>
              <p className="text-sm text-gray-500">
                Visão consolidada das negociações pendentes.
              </p>
            </div>
          </div>
        </div>

        {isLoading && (
          <div
            className="flex min-h-[min(420px,calc(100vh-14rem))] w-full flex-col items-center justify-center gap-4 py-12"
            aria-busy="true"
            aria-live="polite"
          >
            <div
              className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[rgb(228,65,49)]"
              role="status"
              aria-label="Carregando"
            />
            <p className="text-sm text-gray-500">Carregando dados...</p>
          </div>
        )}

        {!isLoading && dashboard && (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">
                  Clientes Pendentes
                </p>
                <p className="text-3xl font-bold text-gray-800">
                  {dashboard.summary.totalCustomers.toLocaleString("pt-BR")}
                </p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">
                  Valor em Aberto
                </p>
                <p className="text-3xl font-bold text-[rgb(228,65,49)]">
                  {numberToCurrency(dashboard.summary.totalAmount)}
                </p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">
                  Atraso Médio
                </p>
                <p className="text-3xl font-bold text-gray-800">
                  {dashboard.summary.averageDaysOverdue.toFixed(1)} dias
                </p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">
                  Maior Atraso
                </p>
                <p className="text-3xl font-bold text-gray-800">
                  {dashboard.summary.maxDaysOverdue.toLocaleString("pt-BR")} dias
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Ranking por Filial
                </h2>
                {dashboard.rankingByCompany.length === 0 ? (
                  <p className="text-sm text-gray-400">Nenhum dado disponível.</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {dashboard.rankingByCompany.map((company, idx) => (
                        <tr
                          key={`${company.companyId}-${idx}`}
                          className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}
                        >
                          <td className="py-2 px-3 text-gray-500 w-8 text-right font-medium">
                            {idx + 1}.
                          </td>
                          <td className="py-2 px-3 text-gray-700 font-medium">
                            {company.companyName}
                          </td>
                          <td className="py-2 px-3 text-gray-500 text-right whitespace-nowrap">
                            {company.totalCustomers} clientes
                          </td>
                          <td className="py-2 px-3 text-gray-700 text-right font-semibold whitespace-nowrap">
                            {numberToCurrency(company.totalAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Maiores Valores em Aberto
                </h2>
                {dashboard.highestDebts.length === 0 ? (
                  <p className="text-sm text-gray-400">Nenhum dado disponível.</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {dashboard.highestDebts.map((item, idx) => (
                        <tr
                          key={`${item.id}-${idx}`}
                          className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}
                        >
                          <td className="py-2 px-3 text-gray-700 font-medium">
                            {item.renterName}
                          </td>
                          <td className="py-2 px-3 text-gray-500 whitespace-nowrap">
                            {item.licensePlate}
                          </td>
                          <td className="py-2 px-3 text-gray-500 text-right whitespace-nowrap">
                            {item.daysOverdue} dias
                          </td>
                          <td className="py-2 px-3 text-gray-700 text-right font-semibold whitespace-nowrap">
                            {numberToCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
}

export default ReceivablesDashboardPage;
